import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { useListPatientsQuery, useLazyListPatientsQuery, useUpdatePatientEngagementMutation, useUpdatePatientMutation } from '../../api/patientsApi';
import { useListDoctorsQuery } from '../../api/usersApi';
import { selectCurrentUser, selectHasRole } from '../auth/authSlice';
import { useInfiniteQuery } from '../../hooks/useInfiniteQuery';
import { fetchAllPages } from '../../utils/fetchAllPages';
import ExportButtons from '../../components/ExportButtons';
import DateRangePicker, { useDateRange } from '../../components/DateRangePicker';
import PatientDetailDrawer from './PatientDetailDrawer';
import { RENEWAL_PITCH_OPTIONS } from './RenewalPitchModal';
import { doctorDisplayName } from '../appointments/doctorColors';
import { isPermissionError } from '../../utils/apiError';

const PATIENT_EXPORT_COLUMNS = [
  { key: 'code', label: 'Patient ID' },
  { key: 'name', label: 'Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'city', label: 'City' },
  { key: 'status', label: 'Status' },
  { key: 'activity', label: 'Activity' },
  { key: 'cohort', label: 'Cohort' },
  { key: 'googleReview', label: 'Google Review' },
  { key: 'renewalPitched', label: 'Renewal Pitched' },
  { key: 'renewalPitchedBy', label: 'Renewal Pitched By' },
  { key: 'created', label: 'Created' },
];

export default function PatientsPage() {
  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState('');
  const [newOnly, setNewOnly] = useState(false);
  // Default: latest patient first, by Patient ID descending (highest code = newest patient).
  const [sortBy, setSortBy] = useState('code');
  const [sortDesc, setSortDesc] = useState(true);
  const [openPatientId, setOpenPatientId] = useState(null);
  // Support deep links of the form /patients?openId=<guid> — e.g. clicking a patient's name
  // inside the appointment calendar popover lands here with the drawer pre-opened.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get('openId');
    if (id) setOpenPatientId(id);
  }, [searchParams]);
  const closeDrawer = () => {
    setOpenPatientId(null);
    if (searchParams.get('openId')) {
      const next = new URLSearchParams(searchParams);
      next.delete('openId');
      setSearchParams(next, { replace: true });
    }
  };
  // Creation-date range filter (default 'custom' with empty dates = no filter / all patients).
  const createdRange = useDateRange('custom');

  // Doctors get a "My Patients / All Patients" toggle. Default to their own patients so they land
  // on the most relevant list; non-doctors always see everyone (toggle hidden).
  const currentUser = useSelector(selectCurrentUser);
  const isDoctor = useSelector(selectHasRole('Doctor'));
  const [mineOnly, setMineOnly] = useState(true);
  const scopeToMine = isDoctor && mineOnly && currentUser?.id;

  const setSort = (col) => {
    if (sortBy === col) setSortDesc((d) => !d);
    else { setSortBy(col); setSortDesc(false); }
  };

  // Filter WITHOUT page/pageSize — the infinite hook owns paging. Changing any of these
  // resets the scroll back to page 1 so sort/search/filter always reflect the full dataset.
  const filter = useMemo(() => ({
    search: search.trim(),
    sortBy,
    sortDesc,
    ...(newOnly ? { newOnly: true } : {}),
    ...(scopeToMine ? { assignedDoctorId: currentUser.id } : {}),
    ...(createdRange.range.from ? { createdFrom: createdRange.range.from } : {}),
    ...(createdRange.range.to ? { createdTo: createdRange.range.to } : {}),
    ...(activity === 'active' ? { isActive: true } : activity === 'inactive' ? { isActive: false } : {}),
  }), [search, sortBy, sortDesc, activity, newOnly, scopeToMine, currentUser?.id, createdRange.range.from, createdRange.range.to]);

  const { items, total, hasMore, isFetching, sentinelRef, patchItem, removeItem } =
    useInfiniteQuery(useListPatientsQuery, filter, { pageSize: 30 });
  const [updateEngagement] = useUpdatePatientEngagementMutation();
  const [updatePatient] = useUpdatePatientMutation();
  const { data: doctors = [] } = useListDoctorsQuery();

  // Export the FULL filtered dataset (loops all pages), not just rows scrolled into view.
  const [triggerList] = useLazyListPatientsQuery();
  const renewalPitchLabels = useMemo(() => Object.fromEntries(
    RENEWAL_PITCH_OPTIONS.map((o) => [o.value, o.label])
  ), []);

  const updateEngagementField = async (id, patch) => {
    try {
      const updated = await updateEngagement({ id, ...patch }).unwrap();
      patchItem(id, updated ?? patch); // reflect inline without waiting for a refetch
    } catch (err) {
      // Don't nag about permission on a quick inline toggle — the control simply no-ops.
      if (isPermissionError(err)) return;
      toast.error(err?.data?.error?.message || 'Could not update patient engagement');
    }
  };

  const STATUS_OPTIONS = [
    { value: 'Active',    label: 'Active',    isActive: true  },
    { value: 'Inactive',  label: 'Inactive',  isActive: false },
    { value: 'Completed', label: 'Completed', isActive: false },
  ];

  const resolveStatus = (p) => {
    if (p.status === 'Completed') return 'Completed';
    return p.isActive ? 'Active' : 'Inactive';
  };

  const updateStatus = async (p, newStatus) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === newStatus);
    if (!opt) return;
    try {
      const updated = await updatePatient({ id: p.id, status: opt.value, isActive: opt.isActive }).unwrap();
      // Update the visible row in place (or drop it if it no longer matches the activity filter)
      // so the change shows without a manual reload.
      const stillMatches =
        activity === '' ||
        (activity === 'active' && (updated?.isActive ?? opt.isActive)) ||
        (activity === 'inactive' && !(updated?.isActive ?? opt.isActive));
      if (stillMatches) patchItem(p.id, updated ?? { status: opt.value, isActive: opt.isActive });
      else removeItem(p.id);
      toast.success(`${p.fullName} marked as ${opt.label}.`);
    } catch (err) {
      if (isPermissionError(err)) return;
      toast.error(err?.data?.error?.message || 'Could not update patient status.');
    }
  };

  const getExportRows = async () => {
    const all = await fetchAllPages(triggerList, filter, 200);
    return all.map((p) => ({
      code: p.patientCode,
      name: p.fullName,
      phone: p.phoneNumber || '',
      city: p.city || '',
      status: (p.status || '').replace(/([A-Z])/g, ' $1').trim(),
      activity: p.isActive ? 'Active' : 'Inactive',
      cohort: p.isNew ? 'New' : (p.isRenewal ? 'Renewal' : ''),
      googleReview: p.googleReviewDone ? 'Yes' : 'No',
      renewalPitched: renewalPitchLabels[p.renewalPitched] || p.renewalPitched || 'Not Yet',
      renewalPitchedBy: p.renewalPitchedByName || p.renewalPitchedById || '',
      created: new Date(p.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }),
    }));
  };

  const SortTh = ({ col, children, style }) => (
    <th
      style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', ...style }}
      onClick={() => setSort(col)}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {children}
        {sortBy === col
          ? (sortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />)
          : <span style={{ width: 12, display: 'inline-block' }} />}
      </span>
    </th>
  );

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Patients</span></div>
        <div className="page-header-row">
          <div>
            <h1>Patients</h1>
            <div className="page-header-sub">
              Active and historical patient records.
              {total != null && ` ${total.toLocaleString()} total.`}
            </div>
          </div>
          <ExportButtons
            filename="patients"
            title="Patient Files"
            subtitle={`${total?.toLocaleString() ?? 0} patients · exported ${new Date().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}`}
            columns={PATIENT_EXPORT_COLUMNS}
            getRows={getExportRows}
            orientation="landscape"
          />
        </div>
      </div>

      <div className="toolbar">
        {isDoctor && (
          <div className="seg-toggle" role="group" aria-label="Patient visibility">
            <button
              type="button"
              className={`seg-btn ${mineOnly ? 'seg-btn--active' : ''}`}
              aria-pressed={mineOnly}
              onClick={() => setMineOnly(true)}
            >
              My Patients
            </button>
            <button
              type="button"
              className={`seg-btn ${!mineOnly ? 'seg-btn--active' : ''}`}
              aria-pressed={!mineOnly}
              onClick={() => setMineOnly(false)}
            >
              All Patients
            </button>
          </div>
        )}
        <div className="search-field">
          <input className="form-control" placeholder="Search name, phone, code…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="form-control" style={{ width: 200 }} value={activity}
          onChange={(e) => setActivity(e.target.value)}>
          <option value="">All patients</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.84rem', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
          title="Show only New patients (created within 32 days, or exactly 1 appointment in 32 days)">
          <input type="checkbox" checked={newOnly} onChange={(e) => setNewOnly(e.target.checked)} />
          <span>New patients only</span>
        </label>
        <span title="Filter by account creation date">
          <DateRangePicker
            preset={createdRange.preset} onPresetChange={createdRange.setPreset}
            customFrom={createdRange.customFrom} customTo={createdRange.customTo} onCustomChange={createdRange.onCustomChange}
            size="sm"
          />
        </span>
      </div>

      <div className="tbl-wrap tbl-elevated">
        <table className="patients-table">
          <thead>
            <tr>
              <SortTh col="code">Patient code</SortTh>
              <SortTh col="name">Name</SortTh>
              <SortTh col="phone">Phone</SortTh>
              <SortTh col="status">Status</SortTh>
              <SortTh col="tenure">Tenure</SortTh>
              <SortTh col="recency">Recency</SortTh>
              <th>Google Review</th>
              <th>Renewal Pitched</th>
              <th>Renewal Pitched By</th>
              <SortTh col="created">Created</SortTh>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setOpenPatientId(p.id)}>
                <td className="td-mono">{p.patientCode}</td>
                <td className="td-bold">
                  {p.fullName}
                  {p.isNew && <span style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '0 6px' }} title="New patient">(N)</span>}
                  <div className="td-muted">{p.city || '—'}</div>
                </td>
                <td className="td-mono">{p.phoneNumber}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <PatientStatusSelect
                    value={resolveStatus(p)}
                    onChange={(v) => updateStatus(p, v)}
                  />
                </td>
                <td className="td-muted" title={p.firstVisitAt ? `First visit: ${new Date(p.firstVisitAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}` : 'No visits yet'}>
                  {p.tenureDays != null ? `${p.tenureDays} d` : '—'}
                </td>
                <td title={p.lastVisitAt ? `Last visit: ${new Date(p.lastVisitAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}` : 'No completed visit'}>
                  {p.recencyDays != null
                    ? <span className={`badge ${p.recencyDays >= 14 ? 'b-red' : p.recencyDays >= 7 ? 'b-amber' : 'b-green'}`}>{p.recencyDays} d</span>
                    : <span className="td-muted">—</span>}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={!!p.googleReviewDone}
                    onChange={async (e) => {
                      e.stopPropagation();
                      await updateEngagementField(p.id, { googleReviewDone: e.target.checked });
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td>
                  <select
                    className="form-control"
                    value={p.renewalPitched || 'NotYet'}
                    onChange={async (e) => {
                      e.stopPropagation();
                      await updateEngagementField(p.id, { renewalPitched: e.target.value });
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="NotYet">Not Yet</option>
                    {RENEWAL_PITCH_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className="form-control"
                    value={p.renewalPitchedById || ''}
                    onChange={async (e) => {
                      e.stopPropagation();
                      await updateEngagementField(p.id, { renewalPitchedById: e.target.value || null });
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">— Select doctor —</option>
                    {doctors.map((d) => (
                      <option key={d.id} value={d.id}>{doctorDisplayName(d.fullName)}</option>
                    ))}
                  </select>
                </td>
                <td className="td-muted">{new Date(p.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
              </tr>
            ))}
            {isFetching && (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 16 }}>
                <div className="skeleton" style={{ height: 18, maxWidth: 240, margin: '0 auto' }} />
              </td></tr>
            )}
            {!isFetching && items.length === 0 && (
              <tr><td colSpan={10}>
                <div className="empty-state">
                  <h3>No patients found</h3>
                  <p>Try a different search or filter.</p>
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
        {/* Infinite-scroll sentinel — loads the next page as it enters view. */}
        {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
      </div>

      {items.length > 0 && (
        <div className="toolbar" style={{ marginTop: 10, justifyContent: 'center' }}>
          <span className="td-muted" style={{ fontSize: '0.8rem' }}>
            Showing {items.length.toLocaleString()} of {total.toLocaleString()}
            {hasMore ? ' — scroll for more' : ''}
          </span>
        </div>
      )}

      {openPatientId && (
        <PatientDetailDrawer patientId={openPatientId} onClose={closeDrawer} />
      )}
    </>
  );
}

// ── Inline status selector — renders as a coloured pill, expands to a native select on click ──
const STATUS_STYLES = {
  Active:    { background: 'var(--green-100)', color: 'var(--green-800, #065f46)' },
  Inactive:  { background: 'var(--gray-100)',  color: 'var(--gray-600)' },
  Completed: { background: 'var(--blue-100)',  color: 'var(--blue-800)' },
};

function PatientStatusSelect({ value, onChange }) {
  const style = STATUS_STYLES[value] ?? STATUS_STYLES.Inactive;
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        title="Click to change patient status"
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          border: 'none',
          borderRadius: 999,
          padding: '3px 22px 3px 9px',
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.03em',
          cursor: 'pointer',
          outline: 'none',
          fontFamily: 'inherit',
          ...style,
        }}
      >
        <option value="Active">Active</option>
        <option value="Inactive">Inactive</option>
        <option value="Completed">Completed</option>
      </select>
      {/* Chevron drawn in CSS so it inherits the pill colour */}
      <span style={{
        position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
        pointerEvents: 'none', fontSize: '0.6rem', color: style.color, lineHeight: 1,
      }}>▾</span>
    </div>
  );
}
